// /app/frontend/src/components/frugal-v2/components/MonthEmailsModal.jsx
// Modal showing all emails received this month (< 180 lines)

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Mail, CheckCircle, XCircle, Clock, ChevronRight, Trash2 } from 'lucide-react';
import * as api from '../services/frugalApi';

const MonthEmailsModal = ({ isOpen, onClose, token, t }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [filter, setFilter] = useState('all'); // all, pending, approved, rejected
  const [deleting, setDeleting] = useState(null);

  const loadEmails = async () => {
    setLoading(true);
    try {
      const result = await api.fetchCurrentMonthEmails(token);
      setData(result);
    } catch (err) {
      console.error('Error loading month emails:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadEmails();
    }
  }, [isOpen, token]);

  const handleDelete = async (e, emailId) => {
    e.stopPropagation();
    if (deleting) return;
    
    setDeleting(emailId);
    try {
      await api.rejectEmail(token, emailId);
      // Refresh the list
      await loadEmails();
      if (selectedEmail?.id === emailId) {
        setSelectedEmail(null);
      }
    } catch (err) {
      console.error('Error deleting email:', err);
    } finally {
      setDeleting(null);
    }
  };

  if (!isOpen) return null;

  const filteredEmails = data?.emails?.filter(email => {
    if (filter === 'all') return true;
    if (filter === 'pending') return email.status === 'pending_review';
    if (filter === 'approved') return email.status === 'approved';
    if (filter === 'rejected') return email.status === 'rejected';
    return true;
  }) || [];

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'text-emerald-400 bg-emerald-500/20';
      case 'rejected': return 'text-rose-400 bg-rose-500/20';
      default: return 'text-amber-400 bg-amber-500/20';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved': return <CheckCircle className="w-4 h-4" />;
      case 'rejected': return <XCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
      <div className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-lg max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <Mail className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h2 className="text-white font-semibold">
                {t('frugal.emails.monthTitle', 'Emails del Mes')}
              </h2>
              <p className="text-xs text-slate-400">
                {data?.month || ''} • {data?.total || 0} {t('frugal.emails.received', 'recibidos')}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-2 p-3 border-b border-white/5">
          {['all', 'pending', 'approved', 'rejected'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1 text-xs rounded-full transition-colors ${
                filter === f 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-white/5 text-slate-400 hover:bg-white/10'
              }`}
            >
              {f === 'all' && `${t('frugal.emails.all', 'Todos')} (${data?.total || 0})`}
              {f === 'pending' && `${t('frugal.emails.pending', 'Pendientes')} (${data?.pending_count || 0})`}
              {f === 'approved' && `${t('frugal.emails.approved', 'Aprobados')} (${data?.approved_count || 0})`}
              {f === 'rejected' && `${t('frugal.emails.rejected', 'Rechazados')} (${data?.rejected_count || 0})`}
            </button>
          ))}
        </div>

        {/* Email list */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full" />
            </div>
          ) : filteredEmails.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              {t('frugal.emails.noEmails')}
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {filteredEmails.map((email, idx) => {
                const isInformative = email.email_type === 'informative' || (!email.parsed_data?.amount && email.parsed_data?.amount !== 0);
                const canDelete = email.status === 'pending_review';
                return (
                <div 
                  key={email.id || idx}
                  className="flex items-center gap-3 p-3 hover:bg-white/5 cursor-pointer"
                  onClick={() => setSelectedEmail(selectedEmail?.id === email.id ? null : email)}
                >
                  {/* Delete button for pending emails */}
                  {canDelete ? (
                    <button
                      onClick={(e) => handleDelete(e, email.id)}
                      disabled={deleting === email.id}
                      className="p-1.5 rounded-lg bg-rose-500/20 hover:bg-rose-500/40 text-rose-400 transition-colors disabled:opacity-50"
                      title={t('frugal.emails.reject', 'Rechazar')}
                    >
                      {deleting === email.id ? (
                        <div className="w-4 h-4 border-2 border-rose-400 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </button>
                  ) : (
                    <div className={`p-1.5 rounded-lg ${getStatusColor(email.status)}`}>
                      {getStatusIcon(email.status)}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">
                      {email.subject || email.raw_email?.subject || t('frugal.emails.noSubject')}
                    </p>
                    <p className="text-xs text-slate-500">
                      {isInformative ? (
                        <span className="text-blue-400">{t('frugal.emails.informative', 'Informativo')}</span>
                      ) : (
                        <>${email.parsed_data?.amount?.toFixed(2) || '0.00'} • {email.parsed_data?.merchant || email.description || '-'}</>
                      )}
                    </p>
                  </div>
                  <ChevronRight className={`w-4 h-4 text-slate-500 transition-transform ${selectedEmail?.id === email.id ? 'rotate-90' : ''}`} />
                </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Selected email detail */}
        {selectedEmail && (
          <div className="border-t border-white/10 p-4 bg-slate-800/50 min-h-[200px] max-h-[300px] overflow-y-auto">
            <p className="text-xs text-slate-400 mb-2">
              {new Date(selectedEmail.created_at).toLocaleString()}
            </p>
            <div className="text-sm text-white whitespace-pre-wrap">
              {selectedEmail.body_html ? (
                <div dangerouslySetInnerHTML={{ __html: selectedEmail.body_html }} />
              ) : (
                selectedEmail.body_text || selectedEmail.raw_email?.body || selectedEmail.subject || t('frugal.emails.noBody')
              )}
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default MonthEmailsModal;
